import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState
} from "react";
import { getRoutePathByPageType } from '@shared/helpers/findPathForSystemSite';
import { useLocation, useNavigate } from 'react-router-dom';
import { validateCraneSerial, searchCraneItems, fetchCraneProduct, fetchCraneNavigation, fetchCraneContentByUrl } from "@shared/helpers/apis/craneHelper";
import { CraneContent, CraneInfo, CraneProduct, CraneSearchApiRequest, CraneSearchApiResponse } from "@shared/types/CraneTypes";
import useCurrentCulture from "@shared/hooks/useCurrentCulture";
import { useQuery } from "@tanstack/react-query";
import { CategoryProductList } from "@shared/types/CategoryTypes.interface";
import { BreadcrumbItem, Content, SiteNavigationItem } from "@shared/types/ContentTypes";

interface CraneContextProps {
    isLoading: boolean;
    error?: Error | null;
    isCraneMode: boolean | null;
    isOutsideCraneMode: boolean | null;
    craneInfo: CraneInfo | null;
    serialNumber?: string | null;
    products?: CategoryProductList;
    craneProduct: CraneProduct | null;
    pageContent: CraneContent | null;  
    searchQuery?: string;
    setSearchQuery: (query: string) => void;
    categoryId?: number;
    setCategoryId: (categoryId: number) => void;
    productId?: number;
    setProductId: (productId: number) => void;
    pageNumber: number;
    setPageNumber: (pageNumber: number) => void;
    pageSize: number;
    setPageSize: (size: number) => void;
    fetchProduct: (productId: string) => Promise<CraneProduct | null>;
    fetchNavigation: () => Promise<SiteNavigationItem[]>;
    setCraneSerial: (craneSerial: string, navigateAfterValidation?: boolean) => void;
    exitCraneMode: () => void;
    resetCranePage: () => void;
    setCategoryByUrl: (url: string) => void;
    fetchCranePageByUrl: (craneSerialNumber: string, url: string) => void;
    breadcrumbs?: Record<number, BreadcrumbItem[]>;
}

interface CraneProviderProps {
    children: React.ReactNode;
}

const CraneContext = createContext<CraneContextProps>({
    isLoading: false,
    error: null,
    isCraneMode: null,
    isOutsideCraneMode: null,
    craneInfo: null,
    serialNumber: null,
    products: undefined,
    craneProduct: null,
    pageContent: null,
    searchQuery: undefined,
    setSearchQuery: () => {},
    categoryId: undefined,
    setCategoryId: () => {},
    productId: undefined,
    setProductId: () => {},
    pageNumber: 1,
    setPageNumber: () => {},
    pageSize: 50,
    setPageSize: () => {},
    fetchProduct: () => Promise.resolve(null),
    fetchNavigation: () => Promise.resolve([]),
    setCraneSerial: () => {},
    exitCraneMode: () => {},
    resetCranePage: () => {},
    setCategoryByUrl: () => {},
    fetchCranePageByUrl: () => {},
    breadcrumbs: undefined
});

export const CraneProvider = ({ children }: CraneProviderProps) => {
    const navigate = useNavigate();
    const { currentCulture } = useCurrentCulture();
    const cranePath = getRoutePathByPageType("Crane") || "/crane/";
    const location = useLocation();
    const defaultPageSize = (window as any).defaultPagingSize || 50;

    const [isCraneMode, setIsCraneMode] = useState<boolean | null>(null);
    const [isOutsideCraneMode, setIsOutsideCraneMode] = useState<boolean | null>(null);
    const [serialNumber, setSerialNumber] = useState<string | null>(null);
    const [craneInfo, setCraneInfo] = useState<CraneInfo | null>(null);   
    const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
    const [productId, setProductId] = useState<number | undefined>(undefined);
    const [categoryId, setCategoryId] = useState<number | undefined>(undefined);
    const [products, setProducts] = useState<CategoryProductList | undefined>(undefined);
    const [pageContent, setPageContent] = useState<Content | null>(null);
    const [craneProduct, setCraneProduct] = useState<CraneProduct | null>(null);
    const [pageNumber, setPageNumber] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(defaultPageSize);
    const [breadcrumbs, setBreadcrumbs] = useState<Record<number, BreadcrumbItem[]> | undefined>(undefined);

    
    const request = useMemo<CraneSearchApiRequest>(() => {
        const req = {
            serialNumber: serialNumber || "",
            productIdOrName: searchQuery || "",
            productId: productId ? productId.toString() : "",
            categoryId: categoryId && categoryId > 0 ? categoryId.toString() : "",
            pageNumber,
            pageSize,
            inStock: null,
            culture: currentCulture
        };
        return req;
    }, [categoryId, currentCulture, pageNumber, pageSize, productId, searchQuery, serialNumber]);

    const {
        data: apiResponse,
        isLoading,
        error,
    } = useQuery<CraneSearchApiResponse>({
        queryKey: ["craneSearch", request],
        queryFn: () => searchCraneItems(request),
        retry: false,
        enabled: new Boolean(isCraneMode && (!!searchQuery || !!categoryId)).valueOf(),
    });

    const mapToCraneProduct = (product: CraneProduct): CraneProduct => {
        return {
            ...product,
            isUnAvailable: new Boolean(product?.lifeCycle?.lifeCycleStateNumber && (product.lifeCycle.lifeCycleStateNumber < 20 || product?.lifeCycle?.lifeCycleStateNumber > 60)).valueOf()
        };
    }

    const fetchProduct = useCallback(async (productId: string) => {
        const response = await fetchCraneProduct(serialNumber || "", productId, currentCulture);
        const mappedProduct = mapToCraneProduct(response.products[0]);
        setCraneProduct(mappedProduct);
        setProductId(mappedProduct.id);
        return mappedProduct;
    }, [currentCulture, serialNumber]);

    const fetchNavigation = useCallback(async () => {
        const response = await fetchCraneNavigation(serialNumber || "", currentCulture);
        setBreadcrumbs(response.breadcrumbs);
        return response.structure || [];

    }, [currentCulture, serialNumber]);

    const handleApiResponse = useCallback((apiResponse: CraneSearchApiResponse) => {
        const { pageCount, totalCount, pageNumber, pageSize } = apiResponse;
        const mapped:CategoryProductList = {
            totalCount: totalCount,
            totalPages: pageCount,
            pageSize: pageSize,
            pageNumber: pageNumber,
            items: apiResponse.products.map(prod => mapToCraneProduct(prod)),
            skipSize: (apiResponse.pageNumber - 1) * apiResponse.pageSize
        }

        setCraneInfo(apiResponse);

        if (mapped.items.length === 1) {
            setCraneProduct(mapped.items[0] as CraneProduct);
        }

        setProducts(mapped);
    }, []);

    const setCraneSerial = useCallback(async (craneSerialNumber: string, navigateAfterValidation: boolean = true) => {
        try {
            const response = await validateCraneSerial(craneSerialNumber, currentCulture);
            setIsCraneMode(true);
            setSerialNumber(craneSerialNumber);
            setCraneInfo(response);
            if (navigateAfterValidation) {
                navigate(`${cranePath}${craneSerialNumber}`);
            }

        } catch (error) {
            // crane serial is invalid - api returned 404
            console.warn('Crane serial not found in database', craneSerialNumber);
            setIsCraneMode(false);
            setSerialNumber(craneSerialNumber);
            setCraneInfo(null);

            // if (navigateAfterValidation) {
            //     navigate(cranePath);
            // }
        } finally {
            setProducts(undefined);
            setCategoryId(undefined);
            setProductId(undefined);
            setSearchQuery(undefined);
        }
    },[currentCulture, navigate, cranePath]);    

    const fetchCranePageByUrl = useCallback((craneSerialNumber: string, url: string) => {
        fetchCraneContentByUrl(url).then(response => {
            const pageId = response?.data?.id ? parseInt(response.data.id, 10) : undefined;
            const pageType = response?.data?.pageType || undefined;
            setPageContent(response?.data || null);

            switch (pageType || "") {
                case "CategoryPage":
                    setSerialNumber(craneSerialNumber);
                    setIsCraneMode(true);
                    setCategoryId(pageId);
                    break;

                case "ProductPage":
                    searchCraneItems({
                        serialNumber: craneSerialNumber,
                        culture: currentCulture,
                        productId: pageId?.toString() || "",
                        productIdOrName: "",
                        categoryId: "",
                        pageNumber: 1,
                        pageSize: 1,
                        inStock: null,
                    }).then(prodResponse => {
                        if (prodResponse.products && prodResponse.products.length > 0) {
                            const craneProduct = mapToCraneProduct(prodResponse.products[0]);
                            handleApiResponse(prodResponse);
                            setSerialNumber(craneSerialNumber);
                            setIsCraneMode(true);
                            setProductId(craneProduct.id);
                        }
                    });
                    break;

                case "ContentPage":
                    // Make sure to load craneInfo on full page reload
                    setCraneSerial(craneSerialNumber, false);
                    break;

                default:
                    console.warn('Unhandled crane page type in fetchCranePageByUrl', pageType);
            }
        }).catch(error => {
            console.error('Error fetching crane content by url', error);
        });
    }, [currentCulture, handleApiResponse, setCraneSerial]);

    const setCategoryByUrl = useCallback((url: string) => {
        fetchCraneContentByUrl(url).then(response => {
            const categoryId = response?.data?.id ? parseInt(response.data.id, 10) : undefined;
            setCategoryId(categoryId);
        }).catch(error => {
            console.error('Error fetching crane content by url', error);
        });
    }, []);

    useEffect(() => {
        if (error) {
            console.error("CraneContext API Error", error);
            return;
        }

        if (apiResponse && !isLoading) {
            handleApiResponse(apiResponse);
        }
    }, [apiResponse, isLoading, error, handleApiResponse]);

    useEffect(() => {
        // Reset pageNumber when filtering occurs
        setPageNumber(1);

        // Clear products on empty request/reset
        if (!searchQuery?.length && !categoryId?.toString().length) {
            setProducts(undefined);
        }
    }, [searchQuery, categoryId]);

    useEffect(() => {
        setIsOutsideCraneMode(location.pathname === "/" || !location.pathname.includes(cranePath));
    }, [location.pathname, cranePath]);


    useEffect(() => {
        const craneRegex = new RegExp(`^${cranePath}\\d+$`);

        if (craneRegex.test(location.pathname)) {
            setCategoryId(undefined);
            setProductId(undefined);
            setSearchQuery(undefined);
            setProducts(undefined);
            setPageContent(null);
        }
    }, [location.pathname, cranePath]);


    const exitCraneMode = () => {
        setIsCraneMode(false);
        setSerialNumber(null);
        setProducts(undefined);
        setCategoryId(undefined);
        setSearchQuery(undefined);
        navigate('/');
    };

    const resetCranePage = () => {
        setSearchQuery(undefined);
        setProducts(undefined);
        setCategoryId(undefined);
        setProductId(undefined);
    };

    return (
        <CraneContext.Provider value={{
            isLoading,
            error,
            isCraneMode,
            isOutsideCraneMode,
            craneInfo,
            serialNumber,
            searchQuery,
            setSearchQuery,
            categoryId,
            setCategoryId,
            productId,
            setProductId,
            setCraneSerial,
            exitCraneMode,
            pageNumber,
            setPageNumber,
            pageSize,
            setPageSize,
            products,
            craneProduct,
            pageContent,
            fetchProduct,
            fetchNavigation,
            resetCranePage,
            setCategoryByUrl,
            fetchCranePageByUrl,
            breadcrumbs
        }}>
            {children}
        </CraneContext.Provider>
    );
};

export const useCrane = () => {
    const context = useContext(CraneContext);
    if (!context) {
        throw new Error("useCrane must be used within a CraneProvider");
    }
    return context;
};